<template>
  <div class="evaluation-data-sidebar" v-if="evaluation">
    <item-header
      v-if="evaluation.isDefaultSimulation && !loadedModel.simulationLocked"
      type="simulation"
      :choosable="true"
      :with-dropdown="true"
      :selected-id="openSimulation?.id"
      @select="selectOpenSimulation"
    />

    <drawer-block :title="$t('labels.diagramConfig')" :open="true">
      <div class="column">
        <chart-type-chooser
          :chart-type="evaluation.chartType"
          @update:chartType="
            evaluationStore.setChartType(evaluation.id, $event)
          "
        />
        <chart-axes-settings
          v-if="evaluation.chartType"
          :chart-type="evaluation.chartType"
          :axes="evaluation.chartSettings"
          :evaluation-id="evaluationId"
          @update:x-axis="evaluationStore.setAxis(evaluation.id, 'x', $event)"
          @update:y-axis="evaluationStore.setAxis(evaluation.id, 'y', $event)"
        />
      </div>
    </drawer-block>

    <drawer-block
      :title="
        $t('objectNames.simulation', chartAxisConfig?.singleSimulation ? 1 : 10)
      "
      :open="true"
    >
      <chart-builder-simulations :chart-uid="`evaluation_${evaluationId}`" />
    </drawer-block>

    <!-- SINGLE ITEM -->
    <drawer-block
      v-if="chartAxisConfig?.singleItem"
      :title="$t('labels.data')"
      :open="true"
    >
      <chart-builder-item-single
        :chart-uid="`evaluation_${evaluationId}`"
        :item-options="itemOptions"
        :selected-item="selectedItem"
        @selectItem="selectItem"
      />
    </drawer-block>

    <drawer-block
      v-else-if="evaluation.chartType && selectedSimulations.length > 0"
      :title="$t('labels.data')"
      :open="true"
    >
      <chart-builder-items :chart-uid="`evaluation_${evaluationId}`" />
    </drawer-block>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import DrawerBlock from "@core/ui/DrawerBlock.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";

import { useEvaluationStore } from "./evaluations.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useChartData } from "../charts/useChartData";
import { useLoadedModel } from "@editor/store/loadedModel.store";

import ChartTypeChooser from "../charts/ChartTypeChooser.vue";
import ChartAxesSettings from "../charts/ChartAxesSettings.vue";
import ChartBuilderSimulations from "../charts/ChartBuilderSimulations.vue";
import ChartBuilderItemSingle from "../charts/ChartBuilderItemSingle.vue";
import ChartBuilderItems from "../charts/ChartBuilderItems.vue";

import type { SelectableChartItem } from "@editor/items/charts/ChartItem";

const props = defineProps<{
  evaluationId: number;
}>();

const evaluationStore = useEvaluationStore();
const { loadedModel } = useLoadedModel();

const evaluation = computed(() => {
  return evaluationStore.items[props.evaluationId];
});

const { chartAxisConfig, selectedSimulations, selectedItem, itemOptions } =
  useChartData(evaluation);

const openSimulation = computed(() => {
  return useSimulationViewStore().simulation;
});

// ======== SINGLE ITEM SELECTION ===========

function selectItem(item: SelectableChartItem | null) {
  evaluationStore.selectItem(props.evaluationId, item);
}

function selectOpenSimulation(simulationId: number | null) {
  useSimulationViewStore().loadSimulation({ simulationId: simulationId });
}
</script>

<style lang="scss">
.evaluation-data-sidebar {
  background-color: var(--color-background-deep);
  box-shadow: 0 0 10px var(--color-shadow-1);
  overflow: auto;

  &__unselectedinput {
    color: var(--color-grey);
  }
}
</style>
